import { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import {
  FISCALITE_AVANCEE_DEFAULTS,
  FISCALITE_CORRECTED_DEFAULTS,
  type RegimeComparison,
  type RegimeResult,
} from '@app/finance';
import { TAX_RESIDENCIES, type RegimeComparisonInput } from '@app/shared';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatEuros, formatRatio } from '@/lib/format';
import { apiErrorMessage } from '@/lib/api';
import { percentToRatio, ratioToPercent } from '@/lib/rate';
import { ENGINE_MMI_2026_CORRECTED, computeRegimeComparison, type MmiEngine } from './engine';
import { EngineSelect, useStoredEngine } from './EngineSelect';
import { useCreateSimulation } from './queries';
import { FR_REGIME_LABELS, TAX_RESIDENCY_LABELS } from './labels';
import { Field } from './Field';

const formSchema = z.object({
  taxResidency: z.enum(TAX_RESIDENCIES),
  purchasePrice: z.number().positive('Requis'),
  notaryFees: z.number().min(0),
  works: z.number().min(0),
  buildingDepreciationYears: z.number().int().min(1).max(100),
  worksDepreciationYears: z.number().int().min(1).max(100),
  annualRentRate: z.number().min(0).max(100),
  annualPriceGrowth: z.number().min(-50).max(50),
  holdingYears: z.number().int().min(1).max(99),
  loanAnnualRate: z.number().min(0).max(25),
  loanYears: z.number().int().min(1).max(60),
  annualCondo: z.number().min(0),
  annualPropertyTax: z.number().min(0),
  annualPno: z.number().min(0),
  accountingLmnp: z.number().min(0),
  accountingSciIs: z.number().min(0),
  cfeShareOfPropertyTax: z.number().min(0).max(100),
  personalTaxRate: z.number().min(0).max(100),
  corporateTaxRate: z.number().min(0).max(100),
  microFoncierAllowance: z.number().min(0).max(100),
  microBicAllowance: z.number().min(0).max(100),
  microBicCeiling: z.number().min(0),
});

type FormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: FormValues = {
  taxResidency: 'non-resident-eea',
  purchasePrice: FISCALITE_AVANCEE_DEFAULTS.purchasePrice,
  notaryFees: FISCALITE_AVANCEE_DEFAULTS.notaryFees,
  works: FISCALITE_AVANCEE_DEFAULTS.works,
  buildingDepreciationYears: FISCALITE_AVANCEE_DEFAULTS.buildingDepreciationYears,
  worksDepreciationYears: FISCALITE_AVANCEE_DEFAULTS.worksDepreciationYears,
  annualRentRate: ratioToPercent(FISCALITE_AVANCEE_DEFAULTS.annualRentRate),
  annualPriceGrowth: ratioToPercent(FISCALITE_AVANCEE_DEFAULTS.annualPriceGrowth),
  holdingYears: FISCALITE_AVANCEE_DEFAULTS.holdingYears,
  loanAnnualRate: ratioToPercent(FISCALITE_AVANCEE_DEFAULTS.loanAnnualRate),
  loanYears: FISCALITE_AVANCEE_DEFAULTS.loanYears,
  annualCondo: FISCALITE_AVANCEE_DEFAULTS.annualCondo,
  annualPropertyTax: FISCALITE_AVANCEE_DEFAULTS.annualPropertyTax,
  annualPno: FISCALITE_AVANCEE_DEFAULTS.annualPno,
  accountingLmnp: FISCALITE_AVANCEE_DEFAULTS.accountingLmnp,
  accountingSciIs: FISCALITE_AVANCEE_DEFAULTS.accountingSciIs,
  cfeShareOfPropertyTax: ratioToPercent(FISCALITE_AVANCEE_DEFAULTS.cfeShareOfPropertyTax),
  personalTaxRate: ratioToPercent(FISCALITE_AVANCEE_DEFAULTS.personalTaxRate),
  corporateTaxRate: ratioToPercent(FISCALITE_AVANCEE_DEFAULTS.corporateTaxRate),
  microFoncierAllowance: ratioToPercent(FISCALITE_AVANCEE_DEFAULTS.microFoncierAllowance),
  microBicAllowance: ratioToPercent(FISCALITE_AVANCEE_DEFAULTS.microBicAllowance),
  // Lu par mmi-2026-corrected seul : sans effet, et masqué, avec mmi-2026.
  microBicCeiling: FISCALITE_CORRECTED_DEFAULTS.microBicCeiling ?? 0,
};

function correctedTaxValues() {
  return {
    corporateTaxRate: ratioToPercent(FISCALITE_CORRECTED_DEFAULTS.corporateTaxRate),
    microBicAllowance: ratioToPercent(FISCALITE_CORRECTED_DEFAULTS.microBicAllowance),
  };
}

function toInput(values: FormValues, engine: MmiEngine): RegimeComparisonInput {
  return {
    taxResidency: values.taxResidency,
    purchasePrice: values.purchasePrice,
    notaryFees: values.notaryFees,
    works: values.works,
    buildingDepreciationYears: values.buildingDepreciationYears,
    worksDepreciationYears: values.worksDepreciationYears,
    annualRentRate: percentToRatio(values.annualRentRate),
    annualPriceGrowth: percentToRatio(values.annualPriceGrowth),
    holdingYears: values.holdingYears,
    loanAnnualRate: percentToRatio(values.loanAnnualRate),
    loanYears: values.loanYears,
    annualCondo: values.annualCondo,
    annualPropertyTax: values.annualPropertyTax,
    annualPno: values.annualPno,
    accountingLmnp: values.accountingLmnp,
    accountingSciIs: values.accountingSciIs,
    cfeShareOfPropertyTax: percentToRatio(values.cfeShareOfPropertyTax),
    personalTaxRate: percentToRatio(values.personalTaxRate),
    corporateTaxRate: percentToRatio(values.corporateTaxRate),
    microFoncierAllowance: percentToRatio(values.microFoncierAllowance),
    microBicAllowance: percentToRatio(values.microBicAllowance),
    ...(engine === ENGINE_MMI_2026_CORRECTED && { microBicCeiling: values.microBicCeiling }),
  };
}

const RESULT_ROWS: { label: string; kind: 'euros' | 'percent'; get: (r: RegimeResult) => number }[] = [
  { label: 'Loyer', kind: 'euros', get: (r) => r.lines.rent },
  { label: 'Charges de copropriété', kind: 'euros', get: (r) => r.lines.condo },
  { label: 'Taxe foncière', kind: 'euros', get: (r) => r.lines.propertyTax },
  { label: 'Assurance PNO', kind: 'euros', get: (r) => r.lines.pno },
  { label: 'Intérêts d’emprunt', kind: 'euros', get: (r) => r.lines.interest },
  { label: 'Amortissement des travaux', kind: 'euros', get: (r) => r.lines.worksDepreciation },
  { label: 'Amortissement du bâti', kind: 'euros', get: (r) => r.lines.buildingDepreciation },
  { label: 'Comptabilité', kind: 'euros', get: (r) => r.lines.accounting },
  { label: 'CFE', kind: 'euros', get: (r) => r.lines.cfe },
  { label: 'Résultat avant impôt', kind: 'euros', get: (r) => r.resultBeforeTax },
  { label: "Taux d'imposition", kind: 'percent', get: (r) => r.taxRate },
  { label: 'Abattement', kind: 'percent', get: (r) => r.allowance },
  { label: 'Base imposable', kind: 'euros', get: (r) => r.taxableBase },
  { label: 'Impôt', kind: 'euros', get: (r) => r.tax },
  { label: 'Capital remboursé', kind: 'euros', get: (r) => r.capitalRepayment },
  { label: 'Amortissements réintégrés', kind: 'euros', get: (r) => r.depreciationAddBack },
];

/** Avertissement, non bloquant : le calcul du régime est affiché malgré tout. */
function ineligibleLabel(r: RegimeResult): string {
  return r.eligible ? '' : 'Plafond de loyers dépassé';
}

function ResultsTable({ comparison }: { comparison: RegimeComparison }) {
  const regimes = Object.values(comparison.regimes);
  // Un régime dont le plafond de loyers est dépassé n'est pas une option : il ne peut pas être le meilleur.
  const bestCashInPocket = Math.max(...regimes.filter((r) => r.eligible).map((r) => r.cashInPocket));

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="p-2 text-left font-medium text-muted-foreground">Ligne</th>
            {regimes.map((r) => (
              <th key={r.regime} className="p-2 text-right font-medium">
                {FR_REGIME_LABELS[r.regime]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {RESULT_ROWS.map((row) => (
            <tr key={row.label} className="border-b border-border/50">
              <td className="p-2 text-muted-foreground">{row.label}</td>
              {regimes.map((r) => (
                <td key={r.regime} className="p-2 text-right tabular-nums">
                  {row.kind === 'percent' ? formatRatio(row.get(r)) : formatEuros(row.get(r))}
                </td>
              ))}
            </tr>
          ))}
          {regimes.some((r) => !r.eligible) && (
            <tr className="border-b border-border/50">
              <td className="p-2 text-muted-foreground">Éligibilité</td>
              {regimes.map((r) => (
                <td key={r.regime} className="p-2 text-right text-destructive">
                  {ineligibleLabel(r)}
                </td>
              ))}
            </tr>
          )}
          <tr className="border-t-2 border-border font-semibold">
            <td className="p-2">Argent dans la poche</td>
            {regimes.map((r) => (
              <td
                key={r.regime}
                className={`p-2 text-right tabular-nums ${r.cashInPocket === bestCashInPocket ? 'bg-accent text-foreground' : ''}`}
              >
                {formatEuros(r.cashInPocket)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function RegimeComparatorForm() {
  const [label, setLabel] = useState('Comparateur — ');
  const [engine, setEngine] = useStoredEngine();
  const { register, control, setValue } = useForm<FormValues>({
    // Le moteur mémorisé fixe aussi les paramètres fiscaux de départ (IS, micro-BIC).
    defaultValues: {
      ...DEFAULT_VALUES,
      ...(engine === ENGINE_MMI_2026_CORRECTED && correctedTaxValues()),
    },
  });
  const values = useWatch({ control });
  const createSimulation = useCreateSimulation();

  const comparison = useMemo(() => {
    const parsed = formSchema.safeParse(values);
    if (!parsed.success) return null;
    try {
      return computeRegimeComparison(toInput(parsed.data, engine), engine);
    } catch {
      return null;
    }
  }, [values, engine]);

  /** Les paramètres fiscaux corrigés (IS 25 %, micro-BIC 50 %) ne sont pas ceux du classeur. */
  const handleEngineChange = (next: MmiEngine) => {
    const tax = next === ENGINE_MMI_2026_CORRECTED ? correctedTaxValues() : DEFAULT_VALUES;
    setValue('corporateTaxRate', tax.corporateTaxRate);
    setValue('microBicAllowance', tax.microBicAllowance);
    setEngine(next);
  };

  const handleSave = () => {
    const parsed = formSchema.safeParse(values);
    if (!parsed.success) {
      toast.error('Corrigez les champs invalides avant d’enregistrer.');
      return;
    }
    createSimulation.mutate(
      { kind: 'regime-comparison', label: label.trim() || 'Comparateur', engineVersion: engine, input: toInput(parsed.data, engine) },
      {
        onSuccess: () => toast.success('Simulation enregistrée'),
        onError: (error) => toast.error(apiErrorMessage(error, 'Échec de l’enregistrement')),
      },
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <form className="flex flex-col gap-4">
        <EngineSelect value={engine} onChange={handleEngineChange} />
        <Field label="Résidence fiscale" htmlFor="taxResidency">
          <Controller
            control={control}
            name="taxResidency"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full max-w-md" id="taxResidency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAX_RESIDENCIES.map((residency) => (
                    <SelectItem key={residency} value={residency}>
                      {TAX_RESIDENCY_LABELS[residency]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>

        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <legend className="col-span-full text-sm font-medium text-muted-foreground">Bien</legend>
          <Field label="Prix d'acquisition (€)" htmlFor="purchasePrice">
            <Input id="purchasePrice" type="number" step="1" min="0" {...register('purchasePrice', { valueAsNumber: true })} />
          </Field>
          <Field label="Frais de notaire (€)" htmlFor="notaryFees">
            <Input id="notaryFees" type="number" step="1" min="0" {...register('notaryFees', { valueAsNumber: true })} />
          </Field>
          <Field label="Travaux (€)" htmlFor="works">
            <Input id="works" type="number" step="1" min="0" {...register('works', { valueAsNumber: true })} />
          </Field>
          <Field label="Amortissement du bâti (années)" htmlFor="buildingDepreciationYears">
            <Input id="buildingDepreciationYears" type="number" step="1" min="1" {...register('buildingDepreciationYears', { valueAsNumber: true })} />
          </Field>
          <Field label="Amortissement des travaux (années)" htmlFor="worksDepreciationYears">
            <Input id="worksDepreciationYears" type="number" step="1" min="1" {...register('worksDepreciationYears', { valueAsNumber: true })} />
          </Field>
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <legend className="col-span-full text-sm font-medium text-muted-foreground">Location et revente</legend>
          <Field label="Loyer annuel (% de travaux + prix)" htmlFor="annualRentRate">
            <Input id="annualRentRate" type="number" step="0.01" min="0" {...register('annualRentRate', { valueAsNumber: true })} />
          </Field>
          <Field label="Hausse annuelle du prix (%)" htmlFor="annualPriceGrowth">
            <Input id="annualPriceGrowth" type="number" step="0.01" {...register('annualPriceGrowth', { valueAsNumber: true })} />
          </Field>
          <Field label="Durée de détention (années)" htmlFor="holdingYears">
            <Input id="holdingYears" type="number" step="1" min="1" {...register('holdingYears', { valueAsNumber: true })} />
          </Field>
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <legend className="col-span-full text-sm font-medium text-muted-foreground">Financement</legend>
          <Field label="Taux du prêt annuel (%)" htmlFor="loanAnnualRate">
            <Input id="loanAnnualRate" type="number" step="0.01" min="0" {...register('loanAnnualRate', { valueAsNumber: true })} />
          </Field>
          <Field label="Durée du prêt (années)" htmlFor="loanYears">
            <Input id="loanYears" type="number" step="1" min="1" {...register('loanYears', { valueAsNumber: true })} />
          </Field>
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <legend className="col-span-full text-sm font-medium text-muted-foreground">Charges annuelles</legend>
          <Field label="Charges de copropriété (€)" htmlFor="annualCondo">
            <Input id="annualCondo" type="number" step="1" min="0" {...register('annualCondo', { valueAsNumber: true })} />
          </Field>
          <Field label="Taxe foncière (€)" htmlFor="annualPropertyTax">
            <Input id="annualPropertyTax" type="number" step="1" min="0" {...register('annualPropertyTax', { valueAsNumber: true })} />
          </Field>
          <Field label="Assurance PNO (€)" htmlFor="annualPno">
            <Input id="annualPno" type="number" step="1" min="0" {...register('annualPno', { valueAsNumber: true })} />
          </Field>
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <legend className="col-span-full text-sm font-medium text-muted-foreground">Comptabilité et fiscalité</legend>
          <Field label="Comptabilité LMNP réel (€)" htmlFor="accountingLmnp">
            <Input id="accountingLmnp" type="number" step="1" min="0" {...register('accountingLmnp', { valueAsNumber: true })} />
          </Field>
          <Field label="Comptabilité SCI à l'IS (€)" htmlFor="accountingSciIs">
            <Input id="accountingSciIs" type="number" step="1" min="0" {...register('accountingSciIs', { valueAsNumber: true })} />
          </Field>
          <Field label="CFE (% de la taxe foncière)" htmlFor="cfeShareOfPropertyTax">
            <Input id="cfeShareOfPropertyTax" type="number" step="1" min="0" {...register('cfeShareOfPropertyTax', { valueAsNumber: true })} />
          </Field>
          <Field label="Taux marginal d'imposition (%)" htmlFor="personalTaxRate">
            <Input id="personalTaxRate" type="number" step="0.1" min="0" {...register('personalTaxRate', { valueAsNumber: true })} />
          </Field>
          <Field label="Impôt sur les sociétés (%)" htmlFor="corporateTaxRate">
            <Input id="corporateTaxRate" type="number" step="0.1" min="0" {...register('corporateTaxRate', { valueAsNumber: true })} />
          </Field>
          <Field label="Abattement micro-foncier (%)" htmlFor="microFoncierAllowance">
            <Input id="microFoncierAllowance" type="number" step="1" min="0" {...register('microFoncierAllowance', { valueAsNumber: true })} />
          </Field>
          <Field label="Abattement micro-BIC (%)" htmlFor="microBicAllowance">
            <Input id="microBicAllowance" type="number" step="1" min="0" {...register('microBicAllowance', { valueAsNumber: true })} />
          </Field>
          {engine === ENGINE_MMI_2026_CORRECTED && (
            <Field label="Plafond de loyers micro-BIC (€)" htmlFor="microBicCeiling">
              <Input id="microBicCeiling" type="number" step="1" min="0" {...register('microBicCeiling', { valueAsNumber: true })} />
            </Field>
          )}
        </fieldset>
      </form>

      {comparison ? (
        <ResultsTable comparison={comparison} />
      ) : (
        <p className="text-sm text-muted-foreground">Corrigez les champs invalides pour voir le résultat.</p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <Field label="Nom de la simulation" htmlFor="label">
          <Input id="label" value={label} onChange={(event) => setLabel(event.target.value)} className="w-64" />
        </Field>
        <Button onClick={handleSave} disabled={createSimulation.isPending || !comparison}>
          {createSimulation.isPending ? 'Enregistrement…' : 'Enregistrer cette simulation'}
        </Button>
      </div>
    </div>
  );
}
